import React, {FC} from 'react';

import {getHasSearchNegatedTerms} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {parseAccountFilter} from '../../../../../srcCommon/inboxes/search/searchHelpers';
import {
  buildSearchQueryFromParts,
  computeIsNegatedTermPresent,
  computeIsNonNegatedTermPresent,
  splitSearchQuery,
  wrapNegatedTermsInQuotes,
} from '../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {SearchNoResultsAccount} from './searchNoResultsAccount';
import {SearchNoResultsMessage, SearchNoResultsMessageProps} from './searchNoResultsMessage';
import {SearchNoResultsNegationMisuse} from './searchNoResultsNegationMisuse';

/*
 * Props.
 */

interface SearchNoResultsProps extends SearchNoResultsMessageProps {}

/*
 * Component.
 */

export const SearchNoResults: FC<SearchNoResultsProps> = (props) => {
  const {panelId, parameters, cellsContext, navigateToSearch} = props;
  const {
    search: {query},
  } = parameters;

  const hasSearchNegatedTermsEnabled = useAppSelector(getHasSearchNegatedTerms);

  if (hasSearchNegatedTermsEnabled) {
    const queryParts = splitSearchQuery(query);
    const isNegatedTermPresent = hasSearchNegatedTermsEnabled && computeIsNegatedTermPresent(queryParts);
    const areNegatedTermsBeingIgnored = isNegatedTermPresent && !computeIsNonNegatedTermPresent(queryParts);

    if (areNegatedTermsBeingIgnored) {
      const queryWithWrappedNegatedTerms = buildSearchQueryFromParts(wrapNegatedTermsInQuotes(queryParts));
      const searchWithWrappedNegatedTerms = () => {
        navigateToSearch({...parameters.search, query: queryWithWrappedNegatedTerms}, cellsContext);
      };

      return (
        <SearchNoResultsNegationMisuse
          originalQuery={query}
          queryWithWrappedNegatedTerms={queryWithWrappedNegatedTerms}
          searchWithWrappedNegatedTerms={searchWithWrappedNegatedTerms}
        />
      );
    }
  }

  // If the query contains the `account` modifier, maybe render a message to update that account.
  const accountAlias = query && parseAccountFilter(query);
  if (accountAlias) {
    return (
      <SearchNoResultsAccount
        panelId={panelId}
        accountAlias={accountAlias}
        parameters={parameters}
        cellsContext={cellsContext}
        navigateToSearch={navigateToSearch}
      />
    );
  }

  return (
    <SearchNoResultsMessage
      panelId={panelId}
      parameters={parameters}
      cellsContext={cellsContext}
      navigateToSearch={navigateToSearch}
    />
  );
};
